export const LAUNCHER_THEME_CATALOG = [
  { id: 'minimal', label: 'Minimal' },
  { id: 'arena', label: 'Arena' },
  { id: 'modern', label: 'Modern' },
] as const;

export type LauncherTheme = (typeof LAUNCHER_THEME_CATALOG)[number]['id'];

export const LAUNCHER_THEMES: readonly LauncherTheme[] =
  LAUNCHER_THEME_CATALOG.map(({ id }) => id);

export const DEFAULT_LAUNCHER_THEME: LauncherTheme = 'minimal';

const LEGACY_THEME_IDS: Readonly<Record<string, LauncherTheme>> = {
  classic: 'minimal',
};

export const isLauncherTheme = (value: unknown): value is LauncherTheme =>
  LAUNCHER_THEMES.includes(value as LauncherTheme);

export const launcherThemeLabel = (theme: LauncherTheme): string =>
  LAUNCHER_THEME_CATALOG.find(({ id }) => id === theme)?.label ?? theme;

const canonicalTheme = (value: unknown): LauncherTheme | undefined => {
  if (isLauncherTheme(value)) return value;
  if (typeof value !== 'string') return undefined;
  return Object.hasOwn(LEGACY_THEME_IDS, value)
    ? LEGACY_THEME_IDS[value]
    : undefined;
};

export const normalizeTheme = (value: unknown): LauncherTheme =>
  canonicalTheme(value) ?? DEFAULT_LAUNCHER_THEME;

export const readDevThemeOverride = (
  search: string,
  isDevelopment: boolean,
): LauncherTheme | undefined => {
  if (!isDevelopment) return undefined;
  return canonicalTheme(new URLSearchParams(search).get('theme'));
};

interface ThemeResolution {
  configured: unknown;
  override?: LauncherTheme;
  onUnknown?: (value: string) => void;
}

export const resolveLauncherTheme = ({
  configured,
  override,
  onUnknown,
}: ThemeResolution): LauncherTheme => {
  if (override) return override;
  const theme = canonicalTheme(configured);
  if (theme) return theme;
  if (typeof configured === 'string' && configured !== '') {
    onUnknown?.(configured);
  }
  return DEFAULT_LAUNCHER_THEME;
};
